/**
 * After customer form POST, the storefront reloads with success/error markup in the page.
 * Scroll that feedback into view with the same offset as the sticky header (see theme.liquid
 * --header-group-height / --header-height). Does not use URL hashes, so query-driven form
 * state from Shopify is unchanged.
 *
 * Later: narrow which block to scroll (e.g. return_to query param) when multiple signups exist.
 */
const getEmailSignupStickyHeaderOffsetPx = () => {
  const style = getComputedStyle(document.body);
  const group = parseFloat(style.getPropertyValue('--header-group-height'));
  const single = parseFloat(style.getPropertyValue('--header-height'));
  if (Number.isFinite(group) && group > 0) return group;
  if (Number.isFinite(single) && single > 0) return single;
  return 0;
};

const scrollEmailSignupFeedbackIntoView = () => {
  const message = document.querySelector('.email-signup__message--success, .email-signup__message--error');
  if (!message) return;

  const offset = getEmailSignupStickyHeaderOffsetPx() + 12;
  const top = message.getBoundingClientRect().top + window.scrollY - offset;

  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
};

if (document.documentElement.dataset.emailSignupFeedbackScroll !== 'true') {
  document.documentElement.dataset.emailSignupFeedbackScroll = 'true';

  const runAfterDom = () => {
    requestAnimationFrame(() => {
      scrollEmailSignupFeedbackIntoView();
      requestAnimationFrame(scrollEmailSignupFeedbackIntoView);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAfterDom, { once: true });
  } else {
    runAfterDom();
  }

  window.addEventListener('load', scrollEmailSignupFeedbackIntoView, { once: true });
}

/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
